import { inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { Config } from '@environments/config';
import { SessionInfo, SlackBot } from '@local/client-contracts';
import { Logger } from '@unleash-tech/js-logger';
import { ServicesRpcService } from './rpc.service';
import { SlackBotRpcInvoker, observable } from '@local/common';
import { ReplaySubject, Observable, firstValueFrom, BehaviorSubject } from 'rxjs';
import { isEqual } from 'lodash';
import { LogService } from './log.service';
import { SessionService } from './session.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { EventsService } from '@shared/services';
import { CONTROL_PAGE_PATH } from 'src/app/bar/utils/constants';
import { addParam } from '@local/ts-infra';

@Injectable({
  providedIn: 'root',
})
export class SlackBotService {
  private readonly BOTS_CACHE_TIME = 55 * 60 * 1000;
  private readonly CHECK_INTERVAL = 60 * 1000;
  private readonly CACHE_TIME = 5 * 60 * 1000;

  private logger: Logger;
  private service: SlackBot.Service;
  private slackUrlDataInitiated: boolean;
  private slackUrlData$ = new ReplaySubject<SlackBot.InstallationResponse>(1);
  private _bots$ = new BehaviorSubject<SlackBot.GetBotsResponse | null>(null);
  private nextFetch: number;
  private running: boolean;
  private nextBotsFetch: number;
  private botsRunning: boolean;
  private botsTimer: NodeJS.Timer;
  private sessionInfo: Signal<SessionInfo> = signal(null);

  isSocketMode: WritableSignal<boolean> = signal(false);

  private eventsService: EventsService = inject(EventsService);

  private get bots(): SlackBot.GetBotsResponse | null {
    return this._bots$.getValue();
  }

  private set bots(value: SlackBot.GetBotsResponse) {
    this._bots$.next(value);
  }

  @observable
  get bots$(): Observable<SlackBot.GetBotsResponse> {
    return this._bots$.asObservable();
  }

  @observable
  installUrl$(accountId: string, token: string, force = false, redirectUrl?: string): Observable<SlackBot.InstallationResponse> {
    this.innerGetUrlData(accountId, token, force, redirectUrl);
    return this.slackUrlData$.asObservable();
  }

  constructor(
    services: ServicesRpcService,
    private logService: LogService,
    private sessionService: SessionService
  ) {
    this.service = services.invokeWith(SlackBotRpcInvoker, 'slackbot');
    this.logger = this.logService.scope('SlackBotService');
    this.sessionInfo = toSignal(this.sessionService.current$);
    this.fetchBotsData();
    this.startBotsTimer();
  }

  private async innerGetUrlData(accountId: string, token: string, force: boolean, redirectUrl?: string): Promise<void> {
    if (!accountId || !token) {
      this.logger.error('missing token or accountId', { accountId, token });
      return;
    }
    if (this.running) {
      return;
    }
    const now = Date.now();
    if (!force && now < this.nextFetch) {
      return;
    }
    try {
      this.running = true;
      let url = Config.slackInstallUrl;
      if (redirectUrl) {
        url += `?rel-redirect=${redirectUrl}`;
      }
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${token}`,
          'x-unleash-acc': accountId,
        },
      });
      const res = (await response.json()) as SlackBot.InstallationResponse;
      this.nextFetch = now + this.CACHE_TIME;
      this.isSocketMode.set(res.isSocketMode);
      if (!this.slackUrlDataInitiated || !isEqual(res, await firstValueFrom(this.slackUrlData$))) {
        this.slackUrlData$.next(res);
        this.slackUrlDataInitiated = true;
      }
    } catch (error) {
      this.logger.error('failed to fetch slack redirect url', error);
    } finally {
      this.running = false;
    }
  }

  async update(request: SlackBot.UpdateBatchInstallationsRequest) {
    await this.service.updateInstallation(request);
    this.innerGetUrlData(this.sessionInfo()?.workspace?.accountId, this.sessionInfo()?.token, true);
  }

  async getCommandsValidation(request: SlackBot.CommandsValidationRequest): Promise<SlackBot.CommandsValidationResponse> {
    return this.service.getCommandsValidation(request);
  }

  async validateChannelInfo(request: SlackBot.ChannelInfoRequest): Promise<SlackBot.ChannelInfoResponse> {
    return this.service.validateChannelInfo(request).catch((error) => {
      return error;
    });
  }

  async getManifest(request: SlackBot.GetManifestRequest): Promise<SlackBot.GetManifestResponse> {
    return this.service.getManifest(request);
  }

  async createCustomApp(request: SlackBot.CreateCustomAppRequest): Promise<string> {
    const bot = await this.service.createCustomApp(request);
    await this.getDeploymentsList(true);
    return bot;
  }

  async getDeploymentsList(force = false): Promise<SlackBot.GetBotsResponse> {
    if (force) {
      const result = await this.fetchBotsData();
      return result ? result : null;
    }

    return this.bots;
  }

  private startBotsTimer(): void {
    this.botsTimer = setInterval(async () => {
      const now = Date.now();
      if (this.nextBotsFetch && now >= this.nextBotsFetch && !this.botsRunning) {
        await this.fetchBotsData();
      }
    }, this.CHECK_INTERVAL);
  }

  private async fetchBotsData(): Promise<SlackBot.GetBotsResponse> {
    const workspaceId = this.sessionInfo()?.workspace?.id;
    if (!workspaceId) {
      return null;
    }
    try {
      this.botsRunning = true;
      const arr = await firstValueFrom(this.service.getDeploymentsList$({ workspaceId }));
      this.bots = arr;
      this.nextBotsFetch = Date.now() + this.BOTS_CACHE_TIME;
      return this.bots;
    } catch (error) {
      this.logger.error('failed to refresh bots', error);
    } finally {
      this.botsRunning = false;
    }
  }

  async updateBot(request: SlackBot.UpdateBotRequest): Promise<SlackBot.ManifestDetails> {
    const updateBot = await this.service.updateBot(request);
    await this.getDeploymentsList(true);
    return updateBot;
  }

  sendEvent(target: string, name?: string, currentSettingsPage?: string) {
    this.eventsService.event('settings.upgrades', {
      target,
      name: name || 'download_button',
      location: { title: currentSettingsPage || null },
    });
  }

  installBot(installationUrl: string, source: 'experiences' | 'assistant', assistantId: string = '') {
    let param;
    if (source === 'assistant') {
      param = `rel-redirect=${CONTROL_PAGE_PATH}/assistant-settings/${assistantId}`;
    } else {
      param = `rel-redirect=${CONTROL_PAGE_PATH}/experiences`;
    }
    const url = addParam(installationUrl, param);
    window.open(url);
    this.sendEvent(null, 'add_to_slack');
  }

  async getSlackInstallations(): Promise<SlackBot.InstallationResponse> {
    return firstValueFrom(this.installUrl$(this.sessionInfo()?.workspace?.accountId, this.sessionInfo()?.token, false));
  }
}
